import {AfterViewInit, Component, HostBinding, Input, OnDestroy, ViewChild} from '@angular/core';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {Router} from '@angular/router';
import {FormControl} from '@angular/forms';
import {ClientService} from '../../shared/client.service';
import {OrganizationService, OrganizationSearchResult} from '../../shared/organization.service';
import {ClientRef, ClientStatus} from '../../interfaces/client';
import {Subscription} from 'rxjs';
import {ListAdderComponent} from '../../controls/list-adder/list-adder.component';

/**
 * The "+ New" dialog on /clients. The user searches the canonical Organization
 * catalog via the typeahead (backed by ES, falling back to SQL). On pick, they
 * choose Track as Prospect (creates a prospect Client, status 2) or Add as
 * Client (creates an active Client, status 1) — both linked to the org. A
 * prospect is now just a Client row, so there is no separate OrganizationTenant
 * conflict flow: the backend store() is idempotent per (tenant, organization)
 * and returns the existing Client on collision.
 *
 * The bst-list-adder [addnew] hook ("Add new" in the dropdown when the search
 * has no match) calls addBlankClient, which opens the same inline Client-create
 * popup used by new-project / new-user. No Organization link is set on that path.
 *
 * Most duplicate clients were born here: someone typed a company that already
 * existed, did not recognise it in the organisation results, and took "Add
 * new" (#1849). So the tenant's own clients whose name matches the search are
 * listed above the results, and picking one returns that client.
 */
@Component({
    selector: 'bst-new-client-dialog',
    templateUrl: './new-client-dialog.component.html',
    styleUrls: ['./new-client-dialog.component.scss'],
    standalone: false,
})
export class NewClientDialogComponent implements AfterViewInit, OnDestroy {

    /**
     * Callers that open this as an inline picker (e.g. the Add-Opportunity
     * form's "+ New client" action) don't want the page yanked out from under
     * them to /clients/:id — they just want the created client back to fill
     * their own select. Only /clients' own "+ New" entry point navigates.
     */
    @Input() navigateOnCreate = true;

    /** Opened from Intake: compact subtitle, and "Create new" keeps the searched name. */
    @Input() @HostBinding('class.intake') intake = false;

    /** Starts the organization search from a known name (Intake: the company in the mail). */
    @Input() initialQuery: string | null = null;
    @ViewChild(ListAdderComponent) private adder: ListAdderComponent<any>;

    public picked: OrganizationSearchResult | null = null;
    public saving = false;
    public errorMessage: string | null = null;
    public pickerControl = new FormControl<OrganizationSearchResult | null>(null);

    /** The tenant's clients whose name matches the search text. */
    public existing: ClientRef[] = [];
    private allClients: Promise<ClientRef[]> | null = null;
    private searchSub: Subscription | null = null;

    constructor(
        protected ref: NbDialogRef<NewClientDialogComponent>,
        protected router: Router,
        protected clientService: ClientService,
        protected orgService: OrganizationService,
        protected dialogService: NbDialogService,
    ) {
        this.pickerControl.valueChanges.subscribe((selection) => {
            // bst-list-adder in single mode emits a single object (or null on clear);
            // in multi mode it would emit an array. Handle both defensively.
            const v = Array.isArray(selection) ? selection[0] ?? null : selection ?? null;
            this.picked = v as OrganizationSearchResult | null;
            this.errorMessage = null;
        });
    }

    ngAfterViewInit(): void {
        this.searchSub = this.adder?.form.get('search').valueChanges.subscribe((q: string) => this.matchExisting(q)) ?? null;
        // The adder searches with this value on focus (autolist).
        if (this.initialQuery) { this.adder?.form.get('search').setValue(this.initialQuery); }
    }

    ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
    }

    /** Lowercase, no accents, no "(Gelderland)", letters and digits only. */
    private static normalize(name: string | null | undefined): string {
        return (name ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .toLowerCase().replace(/\([^)]*\)/g, '').replace(/[^a-z0-9]/g, '');
    }

    private async matchExisting(q: string): Promise<void> {
        const needle = NewClientDialogComponent.normalize(q);
        if (needle.length < 3) {
            this.existing = [];
            return;
        }
        // One lookup per dialog, archived included: an archived duplicate is still a duplicate.
        this.allClients ??= this.clientService.loadAllClientsAnyStatus().catch(() => []);
        const clients = await this.allClients;
        if (NewClientDialogComponent.normalize(this.adder?.form.get('search').value) !== needle) { return; }
        this.existing = clients
            .filter(c => {
                const name = NewClientDialogComponent.normalize(c.name);
                return name.includes(needle) || (name.length >= 3 && needle.includes(name));
            })
            .slice(0, 5);
    }

    /** Use a client that already exists instead of creating one. */
    async pickExisting(client: ClientRef): Promise<void> {
        this.ref.close({mode: 'client', clientId: client.id, name: client.name});
        if (this.navigateOnCreate) {
            await this.router.navigate(['clients', client.id]);
        }
    }

    cancel(): void {
        this.ref.close(null);
    }

    // bst-list-adder's [addnew] hook: shown as a single "Add new" option in the
    // dropdown when the org search has no match. Reuses the same Client-create
    // popup that new-project / new-user use, so the inline-create path matches
    // the rest of the app verbatim.
    public addBlankClient = async (): Promise<any> => {
        const created = this.intake
            ? await this.clientService.openCreateClientDialog(this.dialogService, this.adder?.form.get('search').value || this.initialQuery)
            : await this.clientService.openCreateClientDialog(this.dialogService);
        if (created) {
            this.ref.close({mode: 'client', clientId: created.id, name: created.name});
            if (this.navigateOnCreate) {
                await this.router.navigate(['clients', created.id]);
            }
        }
        return created;
    };

    async trackAsProspect(): Promise<void> {
        await this.createClient(ClientStatus.PROSPECT);
    }

    async addAsClient(): Promise<void> {
        await this.createClient(ClientStatus.ACTIVE);
    }

    /**
     * Create a Client (prospect or active) linked to the picked Organization.
     * The backend store() is idempotent per (tenant, organization): a collision
     * returns the existing Client rather than 409-ing, so both buttons land on
     * the unified /clients/:id detail.
     */
    private async createClient(status: ClientStatus): Promise<void> {
        if (!this.picked || this.saving) { return; }
        this.saving = true;
        this.errorMessage = null;
        try {
            const newClient = await this.clientService.addClient({
                name: this.picked.name,
                status,
                Organization_id: this.picked.id,
            } as any);
            this.ref.close({mode: status === ClientStatus.PROSPECT ? 'prospect' : 'client', clientId: newClient?.id, name: newClient?.name});
            if (this.navigateOnCreate) {
                this.router.navigate(['clients', newClient?.id]);
            }
        } catch (err: any) {
            this.errorMessage = err?.error?.message ?? err?.message ?? 'Could not create client.';
        } finally {
            this.saving = false;
        }
    }
}
